import React, { useState, useMemo, useEffect, useRef } from 'react';
import classes from './PackScreen.module.css';
import SearchAndCategories from './SearchAndCategories';
import ItemDetail from './ItemDetail';
import InlineAdd from './InlineAdd';
import Confetti from './Confetti';
import { db, auth } from '../../config';
import { doc, updateDoc } from 'firebase/firestore';
import useDragItem from '../../hooks/useDragItem';
import { getItemCategories, restoreScrollPosition } from '../../config/helpers';

const DragHandleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M4 7H20M4 12H20M4 17H20" stroke="#c8cccc" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
);

const BoxIcon = ({ color = '#2be494' }) => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
    <path d="M21 8L18 3H6L3 8M21 8H3M21 8V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V8M12 8V21M9 3L8 8M15 3L16 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M5 12L10 17L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path d="M9 6L15 12L9 18" stroke="#565959" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BADGE_CONFIG = {
  hire:    { label: 'Hired',    className: classes.badgeHired },
  offered: { label: 'Offered', className: classes.badgeOffered },
};

// packItems (PackScreen, below) re-sorts packed items to the bottom the
// moment group.items changes — and Firestore's SDK applies a write to its
// local cache (firing this component's onSnapshot listener) near-instantly,
// well before a human registers the tick. Delaying the actual write this
// long gives the checkbox's pop animation (see .checkboxJustToggled) time
// to play — and be seen — before the item jumps to its new position.
const PACK_TOGGLE_ANIMATION_MS = 450;

const PackItem = ({ item, group, onDetail, onShowToast, handleDragStart, handleDragOver, handleDragEnd }) => {
  const isPacked = item?.packed || false;
  const [packed, setPacked] = useState(isPacked);
  const [saving, setSaving] = useState(false);
  const [justToggled, setJustToggled] = useState(false);
  const pendingToggleRef = useRef(null);
  const mountedRef = useRef(true);
  const category = getItemCategories(item)[0];

  useEffect(() => {
    // Reset on every (re-)setup, not just declared once via useRef(true) —
    // React 18 StrictMode deliberately mounts, cleans up, then re-mounts
    // each component once in dev, to surface exactly this kind of bug.
    // Without this line, that phantom cleanup leaves mountedRef.current
    // false forever even though the component is genuinely still mounted —
    // every `if (mountedRef.current) setSaving(false)` below then silently
    // no-ops, so `saving` never clears and the checkbox stays permanently
    // disabled after the very first toggle.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pendingToggleRef.current) clearTimeout(pendingToggleRef.current);
    };
  }, []);

  const { cardRef, isDragging, isDragOver, dragHandlers, dataAttrs } = useDragItem({
    id: item?.id,
    name: item?.name,
    category,
    onDragStart: handleDragStart,
    onDragOver: handleDragOver,
    onDragEnd: handleDragEnd,
  });

  const isHired = item?.organise?.some(o => o.method === 'Hire');
  const isOffered = item?.borrowStatus === 'offered';
  const badge = isHired ? BADGE_CONFIG.hire : isOffered ? BADGE_CONFIG.offered : null;

  const togglePacked = () => {
    if (saving) return;
    const newPacked = !packed;
    // Optimistic + immediate — the checkbox ticks and plays its pop
    // animation right away. The actual write (which is what drives the
    // list re-sort, via group.items) is deliberately held back until the
    // animation's had time to play; see PACK_TOGGLE_ANIMATION_MS above.
    setPacked(newPacked);
    setJustToggled(true);
    setSaving(true);

    pendingToggleRef.current = setTimeout(async () => {
      pendingToggleRef.current = null;
      if (mountedRef.current) setJustToggled(false);
      try {
        const itemIndex = group.items.findIndex(i => i.id === item.id);
        if (itemIndex === -1) return;
        const updatedItems = [...group.items];
        updatedItems[itemIndex] = { ...updatedItems[itemIndex], packed: newPacked };
        const groupRef = doc(db, 'groups', group.id);
        await updateDoc(groupRef, { items: updatedItems });
      } catch (err) {
        console.log('Error updating packed status:', err);
        onShowToast && onShowToast("Couldn't save that change — check your connection and try again.");
        if (mountedRef.current) setPacked(!newPacked);
      }
      if (mountedRef.current) setSaving(false);
    }, PACK_TOGGLE_ANIMATION_MS);
  };

  return (
    <div
      ref={cardRef}
      {...dataAttrs}
      className={`${classes.card} ${packed ? classes.cardPacked : ''} ${isDragging ? classes.cardDragging : ''} ${isDragOver ? classes.cardDragOver : ''}`}
      {...dragHandlers}
    >
      <div className={classes.itemRow}>
        {handleDragStart && (
          <span className={classes.dragHandle} aria-hidden="true">
            <DragHandleIcon />
          </span>
        )}
        <button
          className={`${classes.checkbox} ${packed ? classes.checkboxPacked : ''} ${justToggled ? classes.checkboxJustToggled : ''}`}
          onClick={togglePacked}
          aria-label={packed ? 'Mark as unpacked' : 'Mark as packed'}
          disabled={saving}
        >
          {packed && <CheckIcon />}
        </button>
        <span className={`${classes.itemName} ${packed ? classes.itemNamePacked : ''}`}>
          {item.name}
          {item.ownQuantity > 1 && (
            <span className={classes.itemQty}> ×{item.ownQuantity}</span>
          )}
        </span>
        {badge && (
          <span className={`${classes.badge} ${badge.className}`}>{badge.label}</span>
        )}
        <button
          className={classes.chevronBtn}
          onClick={() => onDetail && onDetail(item)}
          aria-label="Item details"
        >
          <ChevronIcon />
        </button>
      </div>
      {item.lentBy && (
        // Informational, not a status prompting a response (unlike Need's
        // "Offered by" badge) — a muted caption line, always shown once set
        // regardless of packed state. Indented to align under the item
        // name, not the leading handle/checkbox — see .lentByRow's own
        // comment for the exact offset.
        <p className={classes.lentByRow}>Borrowed from {item.lentBy}</p>
      )}
    </div>
  );
};

const PackScreen = ({ group, festivalInfo, activeCategory, setActiveCategory, setCurrentScreen, onAddItem, onShowToast, onAddCategory, onRenameCategory, onDeleteCategory, onSetCategoryIcon, onReorderCategories }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  // ItemDetail intentionally scrolls `.content` to the top on open (see
  // ItemDetail.js) — since it's the same shared scroll container as this
  // list, that leaves the list itself sitting at scrollTop 0 underneath.
  // Captured here right before opening detail, then reapplied on the way
  // back so returning lands on the same item/category instead of the top.
  const savedScrollRef = useRef(0);
  const openItemDetail = (item) => {
    const contentEl = document.querySelector('.content');
    if (contentEl) savedScrollRef.current = contentEl.scrollTop;
    setSelectedItem(item);
  };
  const closeItemDetail = () => {
    setSelectedItem(null);
    restoreScrollPosition(savedScrollRef.current);
  };
  const [inlineAdding, setInlineAdding] = useState(false);
  const preCategoryRef = useRef(activeCategory);

  const user = auth.currentUser;
  const isGuest = user?.isAnonymous;

  // Keep preCategoryRef updated with the last explicitly-selected non-All category
  useEffect(() => {
    if (activeCategory !== '') preCategoryRef.current = activeCategory;
  }, [activeCategory]);

  // Typing a search query while a specific category tab is active would
  // otherwise search only within that category — invisible to the user,
  // and reads as "search is broken" for any match outside it. Switch to
  // All while searching, then restore the category once the query clears.
  const handleSearchChange = (query) => {
    if (query && activeCategory !== '') {
      preCategoryRef.current = activeCategory;
      setActiveCategory('');
    } else if (!query) {
      setActiveCategory(preCategoryRef.current || '');
    }
    setSearchQuery(query);
  };

  // Root.js swaps Home/My Gear/Need/Pack in and out of the same shared
  // `.content` scroll container rather than mounting a fresh scrollable
  // wrapper per screen, so without this Pack opens at whatever scroll
  // position the previous screen was left at. Reapplied on the next frame
  // too — on iOS Safari a tap can leave momentum/rubber-band scrolling still
  // settling on `.content`, which can silently override a scrollTop reset
  // that only happens once, synchronously, on mount.
  useEffect(() => {
    const contentEl = document.querySelector('.content');
    if (contentEl) contentEl.scrollTop = 0;
    const raf = requestAnimationFrame(() => {
      if (contentEl) contentEl.scrollTop = 0;
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  // Icon `stroke` props are drawn as SVG, which can't read CSS variables —
  // derive the value directly from the festival doc, same as Root.js does
  // when it sets the CSS custom property.
  const brandPrimary = festivalInfo?.brandColorPrimary || '#2be494';

  // Drag-and-drop reordering — independent of My Gear's/Need's order fields.
  // Reordering is constrained within the packed/unpacked group (see handleDragOver).
  const [currentDrag, setCurrentDrag] = useState();
  const [currentDragOver, setCurrentDragOver] = useState();
  const [dragWorking, setDragWorking] = useState(false);

  const byPackOrder = (a, b) => {
    const aOrder = a.packOrder !== undefined ? a.packOrder : (a.order ?? 0);
    const bOrder = b.packOrder !== undefined ? b.packOrder : (b.order ?? 0);
    return aOrder - bOrder;
  };

  // Get items where ownQuantity > 0 or method === Bring. Packed items sink
  // to the bottom; within each group, sort by packOrder (falling back to
  // My Gear's `order` for items never manually reordered here).
  const packItems = useMemo(() => {
    if (!group?.items) return [];
    const filtered = group.items.filter(item => {
      const ownQty = item?.ownQuantity !== undefined
        ? item.ownQuantity
        : (item?.organise || []).filter(o => o.method === 'Bring' || !o.method).length;
      return ownQty > 0;
    });
    const unpacked = filtered.filter(item => !item.packed).sort(byPackOrder);
    const packed = filtered.filter(item => item.packed).sort(byPackOrder);
    return [...unpacked, ...packed];
  }, [group]);

  // Filter by category + search — packItems is already correctly ordered,
  // and .filter() preserves relative order.
  const filteredItems = useMemo(() => {
    let items = packItems;
    if (activeCategory) {
      items = items.filter(item => getItemCategories(item).includes(activeCategory));
    }
    if (searchQuery) {
      items = items.filter(item =>
        item.name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    return items;
  }, [packItems, activeCategory, searchQuery]);

  // Group filtered items by category (used in All view only)
  const groupedItems = useMemo(() => {
    if (activeCategory) return null;
    return (group?.categories || [])
      .map(cat => ({ title: cat, items: filteredItems.filter(item => getItemCategories(item).includes(cat)) }))
      .filter(g => g.items.length > 0);
  }, [filteredItems, activeCategory, group?.categories]);

  // Progress calculation
  const packedCount = packItems.filter(item => item.packed).length;
  const totalCount = packItems.length;
  const progressPct = totalCount > 0 ? Math.round((packedCount / totalCount) * 100) : 0;

  // Confetti burst — fires once on the moment the list actually reaches
  // 100%, not on every render while it's already complete (so reopening an
  // already-fully-packed list doesn't replay it).
  const [showConfetti, setShowConfetti] = useState(false);
  const wasAllPackedRef = useRef(null);
  useEffect(() => {
    const allPacked = totalCount > 0 && packedCount === totalCount;
    if (wasAllPackedRef.current === null) {
      wasAllPackedRef.current = allPacked;
      return;
    }
    if (allPacked && !wasAllPackedRef.current) setShowConfetti(true);
    wasAllPackedRef.current = allPacked;
  }, [packedCount, totalCount]);

  // Drag reordering — mirrors Checklist.js's handleDragStart/Over/End, but
  // scoped to packItems and constrained within the packed/unpacked group:
  // an unpacked item can only be reordered among other unpacked items, and
  // likewise for packed items. Reassigns `packOrder` (not `order`), and
  // never changes an item's position in the full group.items array.
  const handleDragStart = (e, id, name, category) => {
    const item = packItems.find(i => i.id === id);
    setCurrentDrag({ id, name, category, packed: !!item?.packed });
  };

  const handleDragOver = (e, id, name, category) => {
    e?.preventDefault();
    const item = packItems.find(i => i.id === id);
    const packed = !!item?.packed;
    // Ignore hovering over the other packed/unpacked group entirely —
    // dragging an unpacked item into the packed group (or vice versa) isn't allowed.
    if (currentDrag && currentDrag.packed !== packed) return;
    // Bail out when the hovered card hasn't actually changed — dragover
    // fires continuously (native drag every frame, touch on every
    // touchmove), and setting a brand-new object every time would
    // re-render the whole list for the entire drag gesture instead of
    // only when there's actually something new to reflect.
    setCurrentDragOver(prev => (prev?.id === id ? prev : { id, name, category, packed }));
  };

  const handleDragEnd = async (e) => {
    setDragWorking(false);

    if (dragWorking || !currentDrag || !currentDragOver) return;
    if (currentDrag.id === currentDragOver.id) return;
    if (currentDrag.packed !== currentDragOver.packed) return; // safety net
    e?.preventDefault();
    setDragWorking(true);

    // Work on a copy of just this packed/unpacked sub-group — reordering
    // here must not touch array position for items outside this group.
    const subGroupItems = packItems.filter(item => !!item.packed === currentDrag.packed);
    const dragIndex = subGroupItems.findIndex(item => item.id === currentDrag.id);
    const dragOverIndex = subGroupItems.findIndex(item => item.id === currentDragOver.id);
    if (dragIndex === -1 || dragOverIndex === -1) {
      setCurrentDrag();
      setCurrentDragOver();
      setDragWorking(false);
      return;
    }
    const dragItemContent = subGroupItems[dragIndex];

    // Dragging into a different category section re-tags the item, same as My Gear
    const dragItemCategories = getItemCategories(dragItemContent);
    if (!dragItemCategories.includes(currentDragOver.category)) {
      const currentCatIndex = dragItemCategories.indexOf(currentDrag.category);
      if (currentCatIndex !== -1) dragItemCategories[currentCatIndex] = currentDragOver.category;
      dragItemContent.categories = dragItemCategories;
    }

    subGroupItems.splice(dragIndex, 1);
    subGroupItems.splice(dragOverIndex, 0, dragItemContent);
    subGroupItems.forEach((item, index) => { item.packOrder = index; });

    // The items in subGroupItems are the same object references as in
    // group.items, already mutated above, so a plain shallow copy persists
    // the packOrder/category changes without disturbing array position.
    if (group?.id) {
      try {
        const groupRef = doc(db, 'groups', group.id);
        await updateDoc(groupRef, { items: [...group.items] });
      } catch (err) {
        alert('There was a problem updating your group.');
      }
    }
    setCurrentDrag();
    setCurrentDragOver();
    setDragWorking(false);
  };

  if (selectedItem) return (
    <ItemDetail item={selectedItem} group={group} onBack={closeItemDetail} />
  );

  const handleAddItem = (cat) => {
    setActiveCategory(cat || activeCategory);
    setInlineAdding(true);
  };

  // Empty state
  if (packItems.length === 0) {
    return (
      <div className={classes.container}>
        <SearchAndCategories
          categories={group?.categories || []}
          categoryIcons={group?.categoryIcons || {}}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          searchQuery={searchQuery}
          setSearchQuery={handleSearchChange}
          onAddItem={handleAddItem}
          onAddCategory={() => onAddCategory && onAddCategory()}
          onRenameCategory={(oldName, newName) => onRenameCategory && onRenameCategory(oldName, newName)}
          onDeleteCategory={(cat) => onDeleteCategory && onDeleteCategory(cat)}
          onSetCategoryIcon={(cat, key) => onSetCategoryIcon && onSetCategoryIcon(cat, key)}
          onReorderCategories={(cats) => onReorderCategories && onReorderCategories(cats)}
        />
        {inlineAdding && (
          <div className={classes.inlineAddWrap}>
            <InlineAdd activeCategory={activeCategory} group={group} onClose={() => setInlineAdding(false)} screen="pack" />
          </div>
        )}
        <div className={classes.emptyState}>
          <div className={classes.emptyIcon}><BoxIcon color={brandPrimary} /></div>
          <p className={classes.emptyText}>Once you've chosen your gear, we'll help you pack it like a pro</p>
          <button
            className={classes.emptyBtn}
            onClick={() => setCurrentScreen('list')}
          >
            See what to bring
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {showConfetti && <Confetti onComplete={() => setShowConfetti(false)} />}
      <SearchAndCategories
        group={group}
        categories={group?.categories || []}
        categoryIcons={group?.categoryIcons || {}}
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
        searchQuery={searchQuery}
        setSearchQuery={handleSearchChange}
        onAddItem={handleAddItem}
        onAddCategory={() => onAddCategory && onAddCategory()}
        onRenameCategory={(oldName, newName) => onRenameCategory && onRenameCategory(oldName, newName)}
        onDeleteCategory={(cat) => onDeleteCategory && onDeleteCategory(cat)}
        onSetCategoryIcon={(cat, key) => onSetCategoryIcon && onSetCategoryIcon(cat, key)}
        onReorderCategories={(cats) => onReorderCategories && onReorderCategories(cats)}
      />

      {/* Progress bar */}
      <div className={classes.progressSection}>
        <div className={classes.progressHeader}>
          <span className={classes.progressLabel}>
            {packedCount === totalCount && totalCount > 0
              ? 'All packed! 🎉'
              : `${packedCount} of ${totalCount} packed`}
          </span>
          <span className={classes.progressPct}>{progressPct}%</span>
        </div>
        <div className={classes.progressBar}>
          <div
            className={classes.progressFill}
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Guest prompt */}
      {isGuest && (
        <div className={classes.guestPrompt}>
          <p className={classes.guestText}>Get ready to pack like a pro for the festival!</p>
          <button className={classes.guestBtn}>Create account</button>
        </div>
      )}

      {/* Pack list */}
      <div className={classes.list}>
        {inlineAdding && (
          <InlineAdd activeCategory={activeCategory} group={group} onClose={() => setInlineAdding(false)} screen="pack" />
        )}
        {activeCategory === '' ? (
          groupedItems.map((grp, gi) => (
            <React.Fragment key={gi}>
              <p className={classes.sectionHeading}>{grp.title}</p>
              {grp.items.map((item) => (
                <PackItem
                  key={item.id}
                  item={item}
                  group={group}
                  onDetail={openItemDetail}
                  onShowToast={onShowToast}
                  handleDragStart={handleDragStart}
                  handleDragOver={handleDragOver}
                  handleDragEnd={handleDragEnd}
                />
              ))}
            </React.Fragment>
          ))
        ) : (
          filteredItems.map((item) => (
            <PackItem
              key={item.id}
              item={item}
              group={group}
              onDetail={openItemDetail}
              onShowToast={onShowToast}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDragEnd={handleDragEnd}
            />
          ))
        )}
        {filteredItems.length === 0 && packItems.length > 0 && (
          <p className={classes.emptyText}>
            {searchQuery
              ? `No results for '${searchQuery}'`
              : `No ${activeCategory} items yet. Tap + to add some`}
          </p>
        )}
      </div>
    </div>
  );
};

export default PackScreen;
